import React, {useCallback, useMemo, useState} from "react";
import useIsMounted from "../../../../../../../../hooks/use-is-mounted";
import Api from "../../../../../../../../../core/services/api/api";
import Button from "../../../../../../../base/button/button";
import IconButton from "../../../../../../../base/button/icon_button";
import {Add, ArrowBack, Remove} from "@mui/icons-material";
import {
    dataGridCellTextProps,
    DataGridColumnAlignments,
    DataGridColumnHighlightingMechanismModes,
    DataGridColumnHighlightingMechanisms,
    DataGridColumnTypes,
    useDataGridSavedState
} from "../../../../../../../../../packages/data-grid";
import PartnerDataGrid from "../../../../../../../../containers/data-grid";
import {
    AddPartSupportRequestInvoicesDataGridColumnNames
} from "../../../../../../../../../core/constants/table/data-grid-column-keys";
import useSearch from "../../../../../../../../hooks/use-search";
import {DefaultPaginationInfo} from "../../../../../../../../../core/constants/enums";
import Form from "../../../../../../../base/form";
import {Col} from "reactstrap";
import {OutlinedInputForm} from "../../../../../../../base/input/outlined_input";
import DateUtils from "../../../../../../../../../core/services/date-utils";
import {MuiRFF, Yup} from '../../../../../../../form';
import ValidateMessages from "../../../../../../../../../core/models/static/texts/vallidate-messages";
import Utils from "../../../../../../../../../core/services/utils/utils";
import ProductInformationPopover from "../../../../../../../app-specific/draggable-popover/product-information-popover";

const AddPartSupportRequestLookupSectionInvoicesSection = ({
                                                               defineValues,
                                                               lookupValues,
                                                               setLookupValues,
                                                               previousSection,
                                                           }) => {
    const [loading, setLoading] = useState(false);
    const [invoices, setInvoices] = useState([]);
    const isMounted = useIsMounted();
    const isSerialized = useMemo(() => !!lookupValues?.isSerialized ?? true, [lookupValues?.isSerialized]);

    const dataGridSaveConfig = useMemo(() =>
            isSerialized
                ? ({
                    name: 'AddPartSupportRequestLookupSectionInvoicesSectionSerializedDataGrid',
                    version: 1,
                })
                : ({
                    name: 'AddPartSupportRequestLookupSectionInvoicesSectionUnSerializedDataGrid',
                    version: 1,
                })
        , [isSerialized])

    const columns = useMemo(() => {
        /**@type {DataGridColumn[]}*/
        const columns = [
            {
                title: 'Invoice No',
                name: AddPartSupportRequestInvoicesDataGridColumnNames.invoiceNo,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.string,
                highlightingMechanism: DataGridColumnHighlightingMechanisms.diff,
                highlightingMechanismMode: DataGridColumnHighlightingMechanismModes.highlight,
            },
            {
                title: 'Date',
                name: AddPartSupportRequestInvoicesDataGridColumnNames.date,
                alignment: DataGridColumnAlignments.right,
                type: DataGridColumnTypes.date,
            },
            {
                title: 'Price',
                name: AddPartSupportRequestInvoicesDataGridColumnNames.price,
                alignment: DataGridColumnAlignments.right,
                type: DataGridColumnTypes.money,
            },
            {
                title: 'Serial No',
                name: AddPartSupportRequestInvoicesDataGridColumnNames.serialNo,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.string,
                highlightingMechanism: DataGridColumnHighlightingMechanisms.diff,
                highlightingMechanismMode: DataGridColumnHighlightingMechanismModes.highlight,
                sortable: false,
                width: 150,
            },
            {
                title: 'Available Qty',
                name: AddPartSupportRequestInvoicesDataGridColumnNames.selectedQty,
                alignment: DataGridColumnAlignments.center,
                type: DataGridColumnTypes.element,
                width: 150
            },
            {
                name: AddPartSupportRequestInvoicesDataGridColumnNames.select,
                alignment: DataGridColumnAlignments.center,
                type: DataGridColumnTypes.element,
                sortable: false,
            },
        ];
        if (isSerialized) {
            return columns.filter(e => ![AddPartSupportRequestInvoicesDataGridColumnNames.selectedQty].includes(e.name));
        } else {
            return columns.filter(e => ![AddPartSupportRequestInvoicesDataGridColumnNames.serialNo].includes(e.name));
        }
    }, [isSerialized])

    const [dataGridColumns, dataGridSortBy, dataGridPageSize, dataGridDensity] = useDataGridSavedState({
        columns: columns,
        name: dataGridSaveConfig.name,
        version: dataGridSaveConfig.version,
    });


    /**
     * Gets the list of drivers by calling the api regarding this action
     * If the function is being called for export, the pagination info is passed as the input
     * @type {(function(UseSearchFunctionArg): Promise<void>)}
     */
    const fetchEntries = useCallback(async (args) => {
        setLoading(true);
        const sorts = [];
        if (!!args.orderBy?.field) {
            sorts.push({
                isDesc: args.orderBy.descending,
                Property: args.orderBy.field,
            })
        }
        const forApi = {
            customerId: defineValues.customer?.id,
            keyword: args.filters?.keyword ?? "",
            partNo: lookupValues.part?.partNumber ?? "",
            sorts: sorts,
            period: {
                startDateTime: args.filters?.startDate,
                endDateTime: args.filters?.endDate,
            },
            pagination: args.paginationInfo,
        }
        const response = await Api.searchInvoicesBasedOnPart(forApi, args.cancelTokenSource?.token);
        if (!isMounted())
            return;
        setLoading(false);
        const res = [];
        const isSerialized = response?.data?.items?.[0]?.serialNumbers !== null;
        if (response?.resultFlag) {
            args.setPaginationInfo(prevState => ({
                ...prevState,
                length: response?.data?.totalCount ?? 0,
            }))
            if (isSerialized) {
                response.data?.items?.forEach(invoice => {
                    (invoice.serialNumbers ?? []).forEach(serial => {
                        res.push({...invoice, serialNo: serial});
                    })
                })
            } else {
                response.data?.items?.forEach(invoice => {
                    res.push(invoice);
                });
            }

        }
        setLookupValues(prevState => ({
            ...prevState,
            isSerialized: isSerialized,
        }));
        setInvoices(res);
    }, [defineValues.customer?.id, isMounted, lookupValues.part?.partNumber, setLookupValues])

    /**
     * Searches among the entries based on the filters from the customer.
     * @type {UseSearchFunction}
     */
    const search = useCallback(async (result) => {
        await fetchEntries(result);
    }, [fetchEntries]);

    const initialPaginationInfo = useMemo(() => dataGridPageSize ? {
        ...DefaultPaginationInfo,
        pageSize: dataGridPageSize
    } : DefaultPaginationInfo, [dataGridPageSize])

    const initialSearchDataSettings = useMemo(() => ({
        paginationInfo: initialPaginationInfo,
        orderBy: dataGridSortBy,
        filters: {},
    }), [initialPaginationInfo, dataGridSortBy]);

    const searchData = useSearch(search, initialSearchDataSettings, false);

    /**
     * If the serialNo of the invoice is the same as the serial number of lookup values, then removes it, otherwise
     * selects it.
     * @param invoice
     */
    const selectOrRemoveInvoiceForSubmission = useCallback((invoice) => {
        const selected = isSerialized
            ? invoice.serialNo === lookupValues?.serialNumber && invoice.invoiceNo === lookupValues.invoiceNo
            : invoice.invoiceNo === lookupValues.invoiceNo;
        if (selected) {
            return setLookupValues(prevState => ({
                ...prevState,
                serialNumber: null,
                invoiceNo: null,
                warehouseCode: null,
                selectedQuantity: null,
            }));
        }
        if (isSerialized) {
            return setLookupValues(prevState => ({
                ...prevState,
                selectedQuantity: null,
                serialNumber: invoice.serialNo,
                invoiceNo: invoice?.invoiceNo,
                warehouseCode: null,
            }))
        }
        setLookupValues(prevState => ({
            ...prevState,
            selectedQuantity: (invoice.count ?? 0)?.toString(),
            serialNumber: null,
            invoiceNo: invoice?.invoiceNo,
            warehouseCode: null,
        }))
    }, [isSerialized, lookupValues?.invoiceNo, lookupValues?.serialNumber, setLookupValues])

    /**
     * Changes the selected quantity of the lookup values for the selected unserialized input.
     * @param newValue {number}
     * @param invoice {any}
     */
    const changeSelectedQuantity = useCallback((newValue, invoice) => {
        setLookupValues(prevState => ({
            ...prevState,
            selectedQuantity: !newValue
                ? null
                : Math.max(
                    Math.min(
                        parseFloat(invoice.orderQty ?? '0'),
                        Number(newValue)
                    ),
                    0
                ).toString(),
        }));
    }, [setLookupValues])

    /**@type {DataGridRow[]}*/
    const dataGridRows = useMemo(() =>
        /**@type {DataGridRow[]}*/
        invoices?.map(invoice => {
            const selected = isSerialized
                ? invoice.serialNo === lookupValues?.serialNumber && invoice.invoiceNo === lookupValues.invoiceNo
                : invoice.invoiceNo === lookupValues.invoiceNo;
            return ({
                key: `${invoice?.invoiceNo}-${invoice?.invoiceDate}-${invoice?.serialNo ?? Utils.createUUID()}`,
                data: invoice,
                cells: {
                    [AddPartSupportRequestInvoicesDataGridColumnNames.invoiceNo]: invoice.invoiceNo,
                    [AddPartSupportRequestInvoicesDataGridColumnNames.serialNo]: invoice.serialNo,
                    [AddPartSupportRequestInvoicesDataGridColumnNames.date]: invoice.invoiceDate,
                    [AddPartSupportRequestInvoicesDataGridColumnNames.price]: invoice.unitPrice,
                    [AddPartSupportRequestInvoicesDataGridColumnNames.selectedQty]:
                        !selected
                            ? invoice.orderQty
                            : <div className='d-flex w-100 align-items-center justify-content-center'>
                                <IconButton
                                    onPressed={() => changeSelectedQuantity((+lookupValues.selectedQuantity ?? 0) - 1, invoice)}
                                    color={'secondary'}
                                >
                                    <Remove className={'icon small'}/>
                                </IconButton>
                                <p {...dataGridCellTextProps}
                                   className={'text-sm mx-4 secondary-color-1 font-weight-semi-bold'}>
                                        <span className={'text-color-3'}>
                                            {lookupValues.selectedQuantity ?? 0}
                                        </span> / {invoice.orderQty ?? 0}
                                </p>
                                <IconButton
                                    onPressed={() => changeSelectedQuantity((+lookupValues.selectedQuantity ?? 0) + 1, invoice)}
                                    color={'secondary'}
                                >
                                    <Add className={'icon small'}/>
                                </IconButton>
                            </div>,
                    [AddPartSupportRequestInvoicesDataGridColumnNames.select]:
                        <Button
                            variant={selected ? 'contained' : 'outlined'}
                            color={'secondary'}
                            className={'px-4 py-1'}
                            onClick={() => selectOrRemoveInvoiceForSubmission(invoice)}
                        >
                            {
                                selected
                                    ? "Remove"
                                    : "Select"
                            }
                        </Button>,
                },
            })
        }) ?? [], [invoices, isSerialized, lookupValues?.serialNumber, lookupValues?.invoiceNo, lookupValues?.selectedQuantity, changeSelectedQuantity, selectOrRemoveInvoiceForSubmission])


    return (
        <>
            <div className={'d-flex flex-column px-4 mb-4'}>
                <div className={'d-flex align-items-start justify-content-start w-100'}>
                    <IconButton
                        onPressed={previousSection}
                        color={'secondary'}
                        style={{marginTop: 2}}
                    >
                        <ArrowBack className={'icon normal'}/>
                    </IconButton>
                    <div className={'d-flex flex-column'}>
                        <ProductInformationPopover
                            partNo={lookupValues?.part?.partNumber}
                            wrapperProps={{className: 'ml-3'}}
                            buttonProps={{className: ' pl-0'}}
                        >
                            <span className={'text-md'}>
                                {lookupValues?.part?.partNumber ?? '--'}
                            </span>
                        </ProductInformationPopover>
                        <p className={'text-sm grey-3 ml-3'}>
                            Back to lookup
                        </p>
                    </div>
                </div>
            </div>
            <SearchSection searchData={searchData}/>
            <PartnerDataGrid
                classNames={{
                    container: 'part-support-invoices-data-grid'
                }}
                {...dataGridSaveConfig}
                initialLayoutState={{
                    columns: columns,
                }}
                rows={dataGridRows}
                columns={dataGridColumns}
                hideExporting
                hideFooterActions
                visibleRows={5}
                state={{
                    loading: {state: loading},
                    density: dataGridDensity,
                    sortBy: searchData.orderBy,
                    pagination: searchData.paginationInfo,
                }}
                onCurrentPageChanged={(_, current) => searchData.setPaginationInfo(prevState => ({...prevState, ...current}))}
                onPageSizeChanged={(_, current) => searchData.setPaginationInfo(prevState => ({...prevState, ...current}))}
                onSortByChanged={(_, current) => searchData.setOrderBy(current)}
            />
        </>
    );
}

const schema = Yup.object().shape({
    keyword: Yup.string().nullable(),
    startDate: Yup.mixed()
        .validDate(ValidateMessages.incorrect)
        .disableFutureDate(ValidateMessages.maxDate('today'), 'day')
        .maxDate(Yup.ref('endDate'), ValidateMessages.lessThan('End Date'))
        .nullable(),
    endDate: Yup.mixed()
        .validDate(ValidateMessages.incorrect)
        .disableFutureDate(ValidateMessages.maxDate('today'), 'day')
        .minDate(Yup.ref('startDate'), ValidateMessages.greaterThan('Start Date'))
        .nullable(),
})
const validate = MuiRFF.makeValidate(schema);
const required = MuiRFF.makeRequired(schema);

const SearchSection = ({searchData}) => {

    /**
     * Submits the form and sets the filters of the search data.
     * @param values {any} form values
     */
    const onSubmit = useCallback((values) => {
        searchData.setFilters({
            ...values,
            startDate: values.startDate ? DateUtils.UTCToServer(values.startDate).format(DateUtils.withoutOffsetFormat) : undefined,
            endDate: values.endDate ? DateUtils.UTCToServer(values.endDate).format(DateUtils.withoutOffsetFormat) : undefined,
        });
    }, [searchData])

    const initialValues = useMemo(() => ({
        ...(searchData.filters ?? {}),
        startDate: searchData.filters?.startDate ? DateUtils.serverToUTC(searchData.filters.startDate) : undefined,
        endDate: searchData.filters?.endDate ? DateUtils.serverToUTC(searchData.filters.endDate) : undefined,
    }), [searchData.filters])

    return (
        <Form
            onSubmit={onSubmit}
            validate={validate}
            className={'d-flex w-100 flex-wrap px-3'}
            initialValues={initialValues}
            render={({values, form}) => {
                const colClassName = 'px-2 mb-4';
                return (
                    <>
                        <Col xs={12} className={colClassName}>
                            <OutlinedInputForm
                                onChange={(e) => form.change(e.target.name, e.target.value)}
                                value={values?.keyword ?? ''}
                                label={'Keywords'}
                                name={'keyword'}
                                type={'text'}
                                required={required.keyword}
                            />
                        </Col>
                        <Col xs={6} className={colClassName}>
                            <MuiRFF.DatePicker
                                name={'startDate'}
                                label={"Start Date"}
                                required={required.startDate}
                                maxDate={values.endDate}
                                disableFuture
                            />
                        </Col>
                        <Col xs={6} className={colClassName}>
                            <MuiRFF.DatePicker
                                name={'endDate'}
                                label={"End Date"}
                                required={required.endDate}
                                minDate={values.startDate}
                                disableFuture
                            />
                        </Col>
                        <Col xs={12}>
                            <Button
                                disabled={searchData.loading}
                                type={'submit'}
                                variant={'contained'}
                                color={'primary'}
                                className='d-flex mx-auto px-5 py-2-5'
                            >
                                {
                                    searchData.loading
                                        ? "Searching..."
                                        : "Search"
                                }
                            </Button>
                        </Col>
                    </>
                );
            }}
        />
    );
}

export default AddPartSupportRequestLookupSectionInvoicesSection;
